'use client'
import React, { useState, useEffect, useCallback } from 'react';
import { generateQuestions } from './actions';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { useRouter } from "next/navigation";
import JobDescriptionSection from './jobDescriptionSection';
import GeneratedQuestionsSection from './generatedQuestionsSection';
import Link from 'next/link';
import { ToastError } from '@/lib/sonnerUtils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import FilterEx from '@/lib/badWordsUtils';
import LoginRedirect from '@/components/LoginRedirect';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';
import AdSterra320x50 from '@/components/adsterra-ui/adsterra_320x50';
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';

export type Question = {
  question: string;
  answer: string;
};

interface IErrorMessages {
  jd: string | null,
  orgName: string | null,
  jobTitle: string | null,
  userId: string | null
}

function RevInterviewQuest() {
  const [ errorMessages, setErrorMessages ] = useState<IErrorMessages>({
      jd: null,
      orgName: null,
      jobTitle:  null,
      userId:  null
    });
    const [jd, setJd] = useState('');
    const [ orgName, setOrgName ] = useState<string>('');
    const [ jobTitle, setJobTitle ] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<Question[] | null>(null);
    const [userId, setUserId] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState(false);
    const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
    const [ validParams, setValidParams ] = useState<boolean>(true);

    const router = useRouter();

    const handleIsProfane = (text: string): string => {
      const filter = new FilterEx();

      if(filter.isProfane(text)) {
        const filteredText = filter.cleanExclude(text);
        // setError("The usage of profane, offensive and inappropriate words is not allowed!");
        ToastError("The usage of profane, offensive and inappropriate words is not allowed!");
        return filteredText;
      } else {
        // setError(null);
        return text;
      }
    }

    const validateParam = (
      paramName: keyof IErrorMessages, 
      paramValue: string | null | undefined, 
      errorMessages: IErrorMessages): boolean => {

      let tempErrorMessages = {...errorMessages};

      if(!paramValue || paramValue.trim() === '') {
        tempErrorMessages[paramName] = 'This field cannot be blank or empty!';
        setErrorMessages(tempErrorMessages);
        return false;
      } else {
        tempErrorMessages[paramName] = null;
        setErrorMessages(tempErrorMessages);
        return true;
      }
    }
    
    // useEffect(() => {    
    //   if (!isLoading && isAuthenticated) {
    //     setUserId(user!.id);
  
    //   } else if(!isLoading && !isAuthenticated) {
    //       router.push("/api/auth/login");
    //   }
    // }, [ isLoading, isAuthenticated ]);
  
    const handleGenerate = async () => {
      setLoading(true);
      // Simulate API Call
      if (!jd) return;
      setLoading(true);
      
      try {
        setValidParams(false);
        setErrorMessages({
          jd: null,
          orgName: null,
          jobTitle:  null,
          userId:  null
        });

        setValidParams(
          validateParam('jobTitle', jobTitle, errorMessages)
          && validateParam('orgName', orgName, errorMessages)
          // && validateParam('jd', jd, errorMessages) 
          && validateParam('userId', userId, errorMessages)
        );

        if(!validParams) {
          ToastError('Please, fill in all required fields.');
          return;
        }

        const aiResults = await generateQuestions(jd, orgName!, jobTitle!, userId!); // Calling the Server Action

        // force user tokens balance component to refresh
		    window.dispatchEvent(new Event('tokens-deducted'));
        
        setResults(aiResults!);

      } catch (error) {
        console.error("AI Generation failed:", error);
        alert("Something went wrong with the AI.");
      } finally {
        setLoading(false);
      }
    };
  
    const handleCopy = useCallback(() => {
      if (results) {
        let techQuestions = "Questions: " + '\n';
        
        results.forEach((q, i) => {
          let qString = "";
          
          qString = `Question ${i+1}: ` + q.question + '\n'
            + "Answer " + q.answer + '\n';
  
        });
  
        const questions = techQuestions;
          
        navigator.clipboard.writeText(questions).then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000); // Reset success message after 2s
        });
      }
    }, [ results ]);

  return (
    <>
      <div className="space-y-8 max-w-7xl mx-auto">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Reverse Interview Questions"}</span>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Reverse Interview Questions Predictor
        </h1>

        <AdSterraIFrameBannerAuto position='top' />

        <div className="text-gray-600 dark:text-gray-400">
            Provide us: 
            <ul className="list-disc list-inside pl-2">
              <li>the job title you are intreviewed for</li>
              <li>the employer's organization name</li>
              <li>and the Job Description</li>
            </ul> 
            and let's suggest some reverse interview questions for you to ask the interviewer.
        </div>
        
        <div className='pt-4 flex flex-col space-y-4'>
          <div className="grid grids-rows gap-4">
            <div className="space-y-2">
              <Label htmlFor="title" className='text-indigo-600'>Job Title</Label>
              <Input type="text" placeholder='Job Title...' value={jobTitle}
                onChange={(e) => {
                  let title = handleIsProfane(e.currentTarget.value);
                  setJobTitle(title);
                }} 
                // disabled={isMyLoading} 
              />
              <Label htmlFor="title" className='text-red-600'>{errorMessages.jobTitle}</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title" className='text-indigo-600'>Organization Name</Label>
              <Input type="text" placeholder='Organization Name...' value={orgName}
                onChange={(e) => {
                  let oName = handleIsProfane(e.currentTarget.value);
                  setOrgName(oName);
                }} 
                // disabled={isMyLoading} 
              />
              <Label htmlFor="title" className='text-red-600'>{errorMessages.orgName}</Label>
            </div>

            <JobDescriptionSection 
              loading={loading} jd={jd} 
              setJd={setJd} handleGenerate={handleGenerate} />

            <hr />
            
            <AdSterra320x50 />

            <hr />            

            {/* Right Side: Results */}
            <GeneratedQuestionsSection 
              copySuccess={copySuccess} results={results} userId={userId} 
              handleCopy={handleCopy} />
            
          </div>
        </div>
      </div>

      <div className='w-full flex justify-center items-center mt-8'>
        <AdSterraNativeBanner />
      </div>
      
      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  );
}

export default RevInterviewQuest